import { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, notifyApiError, INR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import AddCafeModal from "../components/AddCafeModal";
import PlatformOwnersModal from "../components/PlatformOwnersModal";
import CafeAutomationPanel from "../components/CafeAutomationPanel";
import PlatformLightWiringPanel from "../components/PlatformLightWiringPanel";
import ESP32CodeGenerator from "../components/ESP32CodeGenerator";
import PlayersPortalCard from "@/components/PlayersPortalCard";
import {
  LogOut,
  Crown,
  Coins,
  TrendingUp,
  Plus,
  Sparkles,
  AlertTriangle,
  Lock,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  PauseCircle,
  PlayCircle,
  Shield,
} from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ s }) {
  const map = {
    active: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40",
    grace: "bg-amber-500/20 text-amber-200 border-amber-400/40",
    suspended: "bg-rose-500/20 text-rose-300 border-rose-400/40",
    pending_approval: "bg-blue-500/20 text-blue-300 border-blue-400/40",
    rejected: "bg-rose-500/20 text-rose-300 border-rose-400/40",
  };
  const Icon =
    s === "active"
      ? Sparkles
      : s === "grace"
      ? AlertTriangle
      : s === "pending_approval"
      ? Clock
      : s === "rejected"
      ? XCircle
      : Lock;
  const label = s === "pending_approval" ? "pending" : s;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded border ${map[s] || map.suspended}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent = "amber" }) {
  const colors = {
    amber: "border-amber-400/30 text-amber-300",
    emerald: "border-emerald-400/30 text-emerald-300",
    rose: "border-rose-400/30 text-rose-300",
    blue: "border-blue-400/30 text-blue-300",
  };
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-[#121a16] p-5 ${colors[accent]}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-bold">
            {label}
          </div>
          <div className="text-3xl font-black mt-2 text-white tabular-nums">
            {value}
          </div>
          {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
        <Icon className={colors[accent].split(" ").pop() + " w-6 h-6"} />
      </div>
    </div>
  );
}

export default function Platform() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [cafes, setCafes] = useState([]);
  const [pending, setPending] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [active, setActive] = useState(null); // { detail }
  const [activeDevices, setActiveDevices] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payment, setPayment] = useState({
    amount: "",
    payment_mode: "upi",
    notes: "",
    extend_to: "", // optional custom deadline (YYYY-MM-DD) — platform sets this
    use_custom_deadline: false,
    billing_cycle_days: 30, // 15 | 30 — controls period length on the new payment
  });
  const [newTableName, setNewTableName] = useState("");

  // Edit-cafe modal (daily_rate / grace_period / billing_cycle_days)
  const [editCafeOpen, setEditCafeOpen] = useState(false);
  const [editCafeForm, setEditCafeForm] = useState({
    daily_rate: "",
    grace_period_days: "",
    billing_cycle_days: "",
  });

  // Delete-cafe confirmation modal. The user must type the cafe name
  // exactly to enable the destructive button — same guard the backend
  // enforces via `?confirm=<name>`.
  const [deleteCafeOpen, setDeleteCafeOpen] = useState(false);
  const [deleteCafeConfirm, setDeleteCafeConfirm] = useState("");
  const [deleteCafeBusy, setDeleteCafeBusy] = useState(false);

  // Pause-access modal: 1/2/3-day chips. Pauses owner+admin without
  // touching the subscription deadline.
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseDays, setPauseDays] = useState(1);
  const [pauseReason, setPauseReason] = useState("");
  const [pauseBusy, setPauseBusy] = useState(false);

  // Approval modal state
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm, setApproveForm] = useState({ trial_days: 7, tables_count: 4 });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [o, c, p] = await Promise.all([
        api.get("/platform/overview"),
        api.get("/platform/cafes"),
        api.get("/platform/cafes/pending"),
      ]);
      setOverview(o.data);
      setCafes(c.data);
      setPending(p.data);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  const openCafe = async (cafe_id) => {
    try {
      const { data } = await api.get(`/platform/cafes/${cafe_id}`);
      setActive(data);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  const confirmApprove = async () => {
    if (!approveTarget) return;
    setBusy(true);
    try {
      await api.post(`/platform/cafes/${approveTarget.id}/approve`, {
        trial_days: Number(approveForm.trial_days) || 0,
        tables_count: Number(approveForm.tables_count) || 0,
      });
      toast.success(`${approveTarget.name} approved`);
      setApproveTarget(null);
      await refresh();
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/platform/cafes/${rejectTarget.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      toast.success(`${rejectTarget.name} rejected`);
      setRejectTarget(null);
      setRejectReason("");
      await refresh();
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const recordPayment = async () => {
    if (!active) return;
    try {
      const body = {
        amount: Number(payment.amount),
        payment_mode: payment.payment_mode,
        notes: payment.notes || null,
      };
      if (payment.use_custom_deadline && payment.extend_to) {
        body.extend_to = payment.extend_to;
      } else if (payment.billing_cycle_days) {
        // Default path — let the backend pin period_start = today and
        // period_end = today + cycle. Cycle is also persisted on the cafe.
        body.billing_cycle_days = Number(payment.billing_cycle_days);
      }
      await api.post(`/platform/cafes/${active.id}/payments`, body);
      toast.success(
        payment.use_custom_deadline && payment.extend_to
          ? `Payment recorded · deadline set to ${payment.extend_to}`
          : `Payment recorded · ${payment.billing_cycle_days}-day cycle started`
      );
      setPayOpen(false);
      setPayment({
        amount: "",
        payment_mode: "upi",
        notes: "",
        extend_to: "",
        use_custom_deadline: false,
        billing_cycle_days: 30,
      });
      await Promise.all([openCafe(active.id), refresh()]);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  const saveDailyRate = async () => {
    if (!active) return;
    const body = {};
    const rate = Number(editCafeForm.daily_rate);
    const grace = Number(editCafeForm.grace_period_days);
    const cycle = Number(editCafeForm.billing_cycle_days);
    if (!Number.isNaN(rate) && editCafeForm.daily_rate !== "") body.daily_rate = rate;
    if (!Number.isNaN(grace) && editCafeForm.grace_period_days !== "") body.grace_period_days = grace;
    if ([15, 30].includes(cycle)) body.billing_cycle_days = cycle;
    if (Object.keys(body).length === 0) {
      toast.error("Nothing to update");
      return;
    }
    try {
      await api.patch(`/platform/cafes/${active.id}`, body);
      toast.success("Cafe updated");
      setEditCafeOpen(false);
      await Promise.all([openCafe(active.id), refresh()]);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  // Inline-edit helper for the cafe detail dialog. Used by the new
  // "Billing Cycle" selector + "Period Start" date picker so the platform
  // admin can re-anchor the billing window without opening another modal.
  // The backend re-derives `billing_period_end` (and `paid_until`) from the
  // start + cycle on every change.
  const patchActive = async (body, successMsg) => {
    if (!active) return;
    try {
      const { data } = await api.patch(`/platform/cafes/${active.id}`, body);
      // Backend returns the full cafe + computed subscription — merge into
      // the dialog's local state so the UI updates without a full refetch.
      setActive((cur) => (cur ? { ...cur, ...data } : cur));
      if (successMsg) toast.success(successMsg);
      refresh();
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  const deleteCafe = async () => {
    if (!active) return;
    if (deleteCafeConfirm !== active.name) {
      toast.error("Type the cafe name exactly to confirm");
      return;
    }
    setDeleteCafeBusy(true);
    try {
      // Pass the name as a query param — the backend enforces the same
      // confirm-match guard server-side, so a stray DELETE never wipes data.
      await api.delete(
        `/platform/cafes/${active.id}?confirm=${encodeURIComponent(active.name)}`,
      );
      toast.success(`"${active.name}" deleted permanently`);
      setDeleteCafeOpen(false);
      setDeleteCafeConfirm("");
      setActive(null);
      await refresh();
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setDeleteCafeBusy(false);
    }
  };

  const pauseCafe = async () => {
    if (!active) return;
    setPauseBusy(true);
    try {
      await api.post(`/platform/cafes/${active.id}/pause`, {
        days: pauseDays,
        reason: pauseReason || null,
      });
      toast.success(`"${active.name}" paused for ${pauseDays} day(s)`);
      setPauseOpen(false);
      setPauseReason("");
      await Promise.all([openCafe(active.id), refresh()]);
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setPauseBusy(false);
    }
  };

  const resumeCafe = async () => {
    if (!active) return;
    try {
      await api.post(`/platform/cafes/${active.id}/resume`);
      toast.success(`Access resumed for "${active.name}"`);
      await Promise.all([openCafe(active.id), refresh()]);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  const addTable = async () => {
    try {
      await api.post(`/platform/cafes/${active.id}/tables`, {
        name: newTableName || null,
      });
      toast.success("Table added");
      setNewTableName("");
      await Promise.all([openCafe(active.id), refresh()]);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  const removeTable = async (tid) => {
    try {
      await api.delete(`/platform/cafes/${active.id}/tables/${tid}`);
      toast.success("Table removed");
      await Promise.all([openCafe(active.id), refresh()]);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0f0d] text-white">
      <header className="sticky top-0 z-30 w-full px-4 sm:px-6 lg:px-8 pt-4">
        <div className="w-full flex items-center justify-between bg-black/50 backdrop-blur-xl border border-white/10 px-5 py-3 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 grid place-items-center text-black font-black shadow-lg">
              <Crown className="w-4 h-4" />
            </div>
            <div>
              <div className="text-lg font-black tracking-tight">
                Platform Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setOwnersOpen(true)}
              data-testid="platform-owners-btn"
              className="border-amber-400/30 text-amber-200 hover:bg-amber-500/10 font-bold uppercase tracking-widest text-[11px]"
            >
              <Shield className="w-4 h-4 mr-1.5" /> Owners
            </Button>
            <Button
              onClick={() => setAddOpen(true)}
              data-testid="add-cafe-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New Cafe
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="logout-btn"
              className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi
            label="Total Cafes"
            value={overview?.total_cafes ?? 0}
            sub={`${overview?.active ?? 0} active · ${overview?.pending ?? 0} pending · ${overview?.suspended ?? 0} blocked`}
            icon={Crown}
            accent="amber"
          />
          <Kpi
            label="Total Tables"
            value={overview?.total_tables ?? 0}
            sub={`Across all cafes`}
            icon={TrendingUp}
            accent="emerald"
          />
          <Kpi
            label="Lifetime Collected"
            value={INR(overview?.total_revenue_collected ?? 0)}
            sub={`${overview?.total_payments ?? 0} payment(s) recorded`}
            icon={Coins}
            accent="blue"
          />
          <Kpi
            label="Suspended"
            value={overview?.suspended ?? 0}
            sub="Cafes needing payment"
            icon={AlertTriangle}
            accent="rose"
          />
        </section>

        {/* Pending Approvals */}
        {pending.length > 0 && (
          <section
            data-testid="pending-approvals-section"
            className="bg-[#121a16] border border-blue-400/30 rounded-2xl overflow-hidden"
          >
            <div className="p-5 border-b border-white/10 flex items-center justify-between bg-blue-500/5">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-300" />
                <h3 className="text-sm uppercase tracking-[0.25em] text-blue-200 font-bold">
                  Pending Approvals
                </h3>
                <span className="bg-blue-500/20 text-blue-200 text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full border border-blue-400/30">
                  {pending.length} new
                </span>
              </div>
              <span className="text-xs text-gray-500">
                Review &amp; approve to give them access
              </span>
            </div>
            <div className="divide-y divide-white/5">
              {pending.map((c) => (
                <div
                  key={c.id}
                  data-testid={`pending-row-${c.id}`}
                  className="px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-white truncate">{c.name}</div>
                      <StatusBadge s="pending_approval" />
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono mt-1 break-all">
                      {c.owner_name} · {c.owner_email}
                      {c.owner_phone && ` · ${c.owner_country_code || ""} ${c.owner_phone}`}
                    </div>
                    {c.address && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        📍 {c.address}
                      </div>
                    )}
                    {c.registered_at && (
                      <div className="text-[10px] text-gray-600 mt-1 uppercase tracking-widest">
                        Submitted {new Date(c.registered_at).toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      onClick={() => {
                        setRejectTarget(c);
                        setRejectReason("");
                      }}
                      data-testid={`reject-${c.id}`}
                      variant="ghost"
                      className="text-rose-300 hover:bg-rose-500/10 border border-rose-400/30"
                    >
                      <XCircle className="w-4 h-4 mr-1.5" /> Reject
                    </Button>
                    <Button
                      onClick={() => {
                        setApproveTarget(c);
                        setApproveForm({ trial_days: 7, tables_count: 4 });
                      }}
                      data-testid={`approve-${c.id}`}
                      className="bg-emerald-500 hover:bg-emerald-600 text-black font-black uppercase tracking-widest"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="bg-[#121a16] border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-sm uppercase tracking-[0.25em] text-gray-300 font-bold">
              All Cafes
            </h3>
            <span className="text-xs text-gray-500">{cafes.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cafes-table">
              <thead className="text-gray-500 text-[10px] uppercase tracking-[0.2em] bg-black/30">
                <tr>
                  <th className="text-left px-5 py-3">Cafe</th>
                  <th className="text-right px-5 py-3">Tables</th>
                  <th className="text-right px-5 py-3">₹/table/day</th>
                  <th className="text-right px-5 py-3">Monthly</th>
                  <th className="text-left px-5 py-3">Period End</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {cafes.map((c) => {
                  const s = c.subscription || {};
                  const monthly =
                    (c.daily_rate || 0) * (c.tables_count || 0) * 30;
                  return (
                    <tr
                      key={c.id}
                      className="border-t border-white/5 hover:bg-white/[0.02]"
                      data-testid={`cafe-row-${c.id}`}
                    >
                      <td className="px-5 py-3">
                        <div className="font-bold text-white">{c.name}</div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          {c.slug} · {c.owner_email}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right font-mono">
                        {c.tables_count}
                      </td>
                      <td className="px-5 py-3 text-right font-mono">
                        {INR(c.daily_rate)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-amber-300 font-bold">
                        {INR(monthly)}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs">
                        {s.billing_period_end || "—"}
                        {s.status === "active" && s.days_remaining > 0 && (
                          <span className="block text-[10px] text-gray-500">
                            {s.days_remaining} day(s) left
                          </span>
                        )}
                        {s.status === "suspended" && s.days_overdue > 0 && s.days_overdue < 9999 && (
                          <span className="block text-[10px] text-red-300">
                            {s.days_overdue} day(s) overdue
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <StatusBadge s={s.status} />
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCafe(c.id)}
                          data-testid={`manage-${c.id}`}
                          className="text-amber-300 hover:bg-amber-400/10"
                        >
                          Manage
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {cafes.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center text-gray-500 py-10 text-sm"
                    >
                      No cafes yet — click <b>New Cafe</b> to onboard your first
                      one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Cross-club Players Portal — platform sees ALL players + can delete / edit phone */}
        <PlayersPortalCard />
      </main>

      {/* Add cafe */}
      <AddCafeModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={refresh}
      />

      {/* Platform Owners (PIN-gated) */}
      <PlatformOwnersModal
        open={ownersOpen}
        onClose={() => setOwnersOpen(false)}
      />

      {/* Cafe detail */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#121a16] border border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black flex items-center gap-3">
                  {active.name}{" "}
                  <StatusBadge s={active.subscription?.status} />
                </DialogTitle>
                <DialogDescription className="text-gray-400 font-mono text-xs">
                  {active.slug} · {active.owner_email} · {active.owner_phone || "no phone"}
                </DialogDescription>
              </DialogHeader>

              <div
                className="grid grid-cols-2 gap-3 mt-2"
                data-testid="cafe-detail-grid"
              >
                {/* Row 1 — Tables | Rate */}
                <Mini label="Tables" v={active.tables_count} />
                <div className="bg-black/40 border border-white/10 rounded-lg p-3 flex flex-col justify-between min-h-[68px]">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">Rate</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-bold text-white text-sm">
                      ₹{active.daily_rate}/table
                    </div>
                    <button
                      data-testid="edit-rate-btn"
                      onClick={() => {
                        setEditCafeForm({
                          daily_rate: String(active.daily_rate ?? ""),
                          grace_period_days: String(active.grace_period_days ?? ""),
                          billing_cycle_days: String(active.billing_cycle_days ?? active.subscription?.billing_cycle_days ?? "30"),
                        });
                        setEditCafeOpen(true);
                      }}
                      className="text-[10px] uppercase tracking-widest text-amber-300 hover:text-amber-200 font-bold"
                    >
                      Edit
                    </button>
                  </div>
                </div>

                {/* Row 2 — Monthly Cost | Revenue */}
                <Mini
                  label="Monthly Cost"
                  v={INR((active.subscription?.daily_cost || 0) * 30)}
                />
                <Mini
                  label="Revenue (cafe)"
                  v={INR(active.stats?.revenue_collected)}
                />

                {/* Row 3 — Registered (read-only) | Billing Cycle (15/30) */}
                <Mini
                  label="Registered"
                  v={
                    active.subscription?.registered_at
                      ? new Date(active.subscription.registered_at).toLocaleDateString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                        })
                      : "—"
                  }
                />
                <div
                  className="bg-black/40 border border-white/10 rounded-lg p-3 flex flex-col justify-between min-h-[68px]"
                  data-testid="billing-cycle-tile"
                >
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">
                    Billing Cycle
                  </div>
                  <div className="flex gap-1.5 mt-1">
                    {[15, 30].map((c) => {
                      const current = Number(
                        active.billing_cycle_days ??
                          active.subscription?.billing_cycle_days ??
                          30,
                      );
                      const selected = current === c;
                      return (
                        <button
                          key={c}
                          data-testid={`billing-cycle-${c}-btn`}
                          onClick={() =>
                            !selected &&
                            patchActive(
                              { billing_cycle_days: c },
                              `Billing cycle set to ${c} days`,
                            )
                          }
                          className={`flex-1 px-2 py-1 rounded-md text-xs font-bold uppercase tracking-widest border ${
                            selected
                              ? "border-amber-400 bg-amber-400/15 text-amber-200"
                              : "border-white/10 text-gray-400 hover:border-white/20"
                          }`}
                        >
                          {c}d
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Row 4 — Period Start (editable) | Period End (read-only, computed) */}
                <PeriodStartTile
                  value={active.subscription?.billing_period_start?.slice(0, 10) || ""}
                  onCommit={(v) =>
                    patchActive(
                      { billing_period_start: v },
                      "Billing period updated",
                    )
                  }
                />
                <Mini
                  label="Period End"
                  v={
                    active.subscription?.billing_period_end
                      ? new Date(active.subscription.billing_period_end).toLocaleDateString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                        })
                      : "—"
                  }
                />
              </div>

              {/* Tables list */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-gray-300">
                    Tables ({active.tables?.length || 0})
                  </h4>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={`Table ${(active.tables?.length || 0) + 1}`}
                      value={newTableName}
                      onChange={(e) => setNewTableName(e.target.value)}
                      data-testid="new-table-name-input"
                      className="bg-black/40 border-white/10 text-white text-sm w-44"
                    />
                    <Button
                      onClick={addTable}
                      data-testid="add-table-btn"
                      className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold uppercase tracking-wider text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {active.tables?.map((t) => (
                    <div
                      key={t.id}
                      className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-bold text-sm">{t.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">
                          {t.status}
                        </div>
                      </div>
                      <button
                        onClick={() => removeTable(t.id)}
                        disabled={t.status === "running"}
                        className="text-gray-500 hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={
                          t.status === "running" ? "Cannot delete running" : "Remove"
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payments */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-gray-300">
                    Subscription Payments ({active.payments?.length || 0})
                  </h4>
                  <Button
                    onClick={() => {
                      // Pre-fill the amount with what's actually owed for the
                      // active cafe's current billing cycle (gross) minus any
                      // pause-credit refund so the platform admin only has to
                      // click OK in the typical case.
                      const cycle = Number(
                        active?.billing_cycle_days ??
                          active?.subscription?.billing_cycle_days ??
                          30,
                      );
                      const dailyCost = Number(
                        active?.subscription?.daily_cost || 0,
                      );
                      const rate = Number(active?.daily_rate || 0);
                      const tables = Number(active?.tables_count || 0);
                      const pauseCredit = Math.max(
                        0,
                        Number(active?.pending_pause_credit_days || 0),
                      );
                      const gross = dailyCost * cycle;
                      const discount = rate * tables * pauseCredit;
                      const suggested = Math.max(0, +(gross - discount).toFixed(2));
                      setPayment((p) => ({
                        ...p,
                        amount: suggested > 0 ? String(suggested) : "",
                        billing_cycle_days: cycle,
                      }));
                      setPayOpen(true);
                    }}
                    data-testid="record-payment-btn"
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs"
                  >
                    <Coins className="w-3 h-3 mr-1" /> Record Payment
                  </Button>
                </div>
                <div
                  className="bg-black/40 border border-white/10 rounded-lg overflow-x-auto"
                  data-testid="subscription-payments-table"
                >
                  {active.payments?.length ? (
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-widest text-gray-500 bg-black/30">
                        <tr>
                          <th className="text-left px-3 py-2">Start</th>
                          <th className="text-left px-3 py-2">End</th>
                          <th className="text-right px-3 py-2">Total Days</th>
                          <th className="text-right px-3 py-2">Paused Days</th>
                          <th className="text-right px-3 py-2">Billed Days</th>
                          <th className="text-right px-3 py-2">Tables</th>
                          <th className="text-right px-3 py-2">Rate / Table</th>
                          <th className="text-right px-3 py-2">Total / Day</th>
                          <th className="text-right px-3 py-2">Total Charged</th>
                          <th className="text-right px-3 py-2">Mode</th>
                          <th className="text-right px-3 py-2">Paid</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5 font-mono">
                        {active.payments.map((p) => {
                          const days = Number(p.days_added || 0);
                          const rate = Number(p.daily_rate_at_time || 0);
                          const tables = Number(p.tables_at_time || 0);
                          const paused = Number(p.paused_days_credit || 0);
                          const billedDays = Math.max(0, days - paused);
                          // Per the platform admin's formula:
                          //   total / day      = tables × rate
                          //   total charged    = total/day × billed_days
                          // Computed (not pulled from p.amount) so the table
                          // always reflects the canonical billing formula
                          // even if a payment was recorded with an off amount.
                          const totalPerDay = +(rate * tables).toFixed(2);
                          const totalCharged = +(totalPerDay * billedDays).toFixed(2);
                          return (
                            <tr
                              key={p.id}
                              data-testid={`payment-row-${p.id}`}
                              className="hover:bg-white/[0.02]"
                            >
                              <td className="px-3 py-2 text-white">
                                {p.billing_period_start || "—"}
                              </td>
                              <td className="px-3 py-2 text-white">
                                {p.billing_period_end || p.extended_to || "—"}
                              </td>
                              <td className="px-3 py-2 text-right text-white">
                                {days}
                              </td>
                              <td
                                className="px-3 py-2 text-right"
                                data-testid={`payment-paused-days-${p.id}`}
                              >
                                {paused > 0 ? (
                                  <span className="text-emerald-300 font-bold">
                                    {paused}
                                  </span>
                                ) : (
                                  <span className="text-gray-600">0</span>
                                )}
                              </td>
                              <td
                                className="px-3 py-2 text-right text-amber-200 font-bold"
                                data-testid={`payment-billed-days-${p.id}`}
                              >
                                {billedDays}
                              </td>
                              <td
                                className="px-3 py-2 text-right text-white"
                                data-testid={`payment-tables-${p.id}`}
                              >
                                {tables}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-300">
                                {INR(rate)}
                                <span className="block text-[9px] text-gray-500 normal-case">
                                  /table/day
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-gray-200">
                                {INR(totalPerDay)}
                                <span className="block text-[9px] text-gray-500 normal-case">
                                  {tables}×₹{rate}
                                </span>
                              </td>
                              <td
                                className="px-3 py-2 text-right font-bold text-amber-300"
                                data-testid={`payment-total-charged-${p.id}`}
                              >
                                {INR(totalCharged)}
                                <span className="block text-[9px] text-gray-500 normal-case font-normal">
                                  ₹{totalPerDay}×{billedDays}d
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right uppercase text-gray-400 text-[10px] tracking-widest">
                                {p.payment_mode}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-500 text-[10px]">
                                {p.paid_at
                                  ? new Date(p.paid_at).toLocaleDateString("en-IN")
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center text-gray-500 text-sm py-4">
                      No payments recorded yet
                    </div>
                  )}
                </div>
              </div>

              {/* Pause / Resume access — temporary block separate from billing */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-start justify-between gap-4 bg-amber-500/5 border border-amber-400/30 rounded-lg p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <PauseCircle className="w-4 h-4 text-amber-300" />
                      <div className="text-sm font-black text-amber-200 uppercase tracking-widest">
                        Pause Access
                      </div>
                    </div>
                    {active.subscription?.status === "paused" ? (
                      <div className="text-[12px] text-amber-100 leading-relaxed">
                        Currently <b className="text-amber-200">paused</b> — owner &amp;
                        admin are blocked. Auto-resumes in{" "}
                        <b>{active.subscription.days_remaining}</b> day(s) (on{" "}
                        <span className="font-mono">
                          {active.subscription.pause_until
                            ? new Date(active.subscription.pause_until).toLocaleString("en-IN")
                            : "—"}
                        </span>
                        ){active.subscription.pause_reason ? (
                          <>. Reason:{" "}
                            <i className="text-amber-100/80">
                              {active.subscription.pause_reason}
                            </i>
                          </>
                        ) : (
                          "."
                        )}
                      </div>
                    ) : (
                      <div className="text-[12px] text-amber-100/70 leading-relaxed">
                        Temporarily block owner &amp; admin access without
                        touching the subscription deadline. Useful for short
                        compliance holds, payment disputes, or end-of-day
                        cool-downs.
                      </div>
                    )}
                  </div>
                  {active.subscription?.status === "paused" ? (
                    <Button
                      type="button"
                      data-testid="resume-access-btn"
                      onClick={resumeCafe}
                      className="bg-emerald-500 hover:bg-emerald-600 text-black shrink-0 uppercase tracking-widest text-xs font-black"
                    >
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Resume now
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      data-testid="pause-access-btn"
                      onClick={() => {
                        setPauseDays(1);
                        setPauseReason("");
                        setPauseOpen(true);
                      }}
                      className="bg-amber-500 hover:bg-amber-600 text-black shrink-0 uppercase tracking-widest text-xs font-black"
                    >
                      <PauseCircle className="w-4 h-4 mr-2" />
                      Pause access
                    </Button>
                  )}
                </div>
              </div>

              {/* Hardware Automation — ESP32 boards, per-cafe gate */}
              <CafeAutomationPanel
                cafe={active}
                api={api}
                onUpdate={(updatedCafe) => {
                  setActive((prev) =>
                    prev ? { ...prev, ...updatedCafe } : prev,
                  );
                  // Keep the cafes list in sync so the toggle column updates.
                  setCafes((list) =>
                    list.map((c) =>
                      c.id === updatedCafe.id ? { ...c, ...updatedCafe } : c,
                    ),
                  );
                }}
                onDevicesChange={setActiveDevices}
              />

              {/* Table Light Wiring — moved from Owner console to Platform */}
              <PlatformLightWiringPanel
                cafeId={active.id}
                enabled={!!active.automation_enabled}
              />

              {/* ESP32 firmware code generator — copy/paste for technicians */}
              <ESP32CodeGenerator
                cafe={active}
                devices={activeDevices}
                enabled={!!active.automation_enabled}
              />

              {/* Danger Zone — permanent cafe deletion */}
              <div className="mt-6 pt-6 border-t border-rose-500/20">
                <div className="flex items-start justify-between gap-4 bg-rose-500/5 border border-rose-500/30 rounded-lg p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-rose-400" />
                      <div className="text-sm font-black text-rose-200 uppercase tracking-widest">
                        Danger Zone
                      </div>
                    </div>
                    <div className="text-[12px] text-rose-100/70 leading-relaxed">
                      Permanently remove this cafe (e.g. if the club has shut
                      down). Deletes the cafe record, all tables, sessions,
                      players, settings, payments, and the owner + staff user
                      accounts. <b>This cannot be undone.</b>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    data-testid="delete-cafe-btn"
                    onClick={() => {
                      setDeleteCafeConfirm("");
                      setDeleteCafeOpen(true);
                    }}
                    className="bg-rose-600 hover:bg-rose-500 text-white shrink-0 uppercase tracking-widest text-xs font-black"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete cafe
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Record payment */}
      <Dialog open={payOpen} onOpenChange={(v) => !v && setPayOpen(false)}>
        <DialogContent className="bg-[#121a16] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">
              Record Payment
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {active?.name} · ₹{active?.subscription?.daily_cost}/day
            </DialogDescription>
          </DialogHeader>

          {/* Current period + next period summary */}
          {active && (() => {
            const cycle = Number(
              active.billing_cycle_days ??
                active.subscription?.billing_cycle_days ??
                30,
            );
            const curStart = active.subscription?.billing_period_start ||
              active.billing_period_start || null;
            const curEnd = active.subscription?.billing_period_end ||
              active.billing_period_end ||
              active.subscription?.paid_until ||
              active.paid_until || null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            // Next period starts at max(today, curEnd)
            let nextStart;
            if (curEnd) {
              const ce = new Date(curEnd + "T00:00:00");
              nextStart = ce > today ? ce : today;
            } else {
              nextStart = today;
            }
            const nextEnd = new Date(nextStart);
            nextEnd.setDate(nextEnd.getDate() + cycle);
            const fmt = (d) =>
              d instanceof Date
                ? d.toISOString().slice(0, 10)
                : (d ? String(d).slice(0, 10) : "—");
            const dailyCost = Number(active.subscription?.daily_cost || 0);
            const rate = Number(active.daily_rate || 0);
            const tables = Number(active.tables_count || 0);
            const grossAmount = +(dailyCost * cycle).toFixed(2);
            const pauseCredit = Math.max(
              0,
              Number(active.pending_pause_credit_days || 0),
            );
            const pauseDiscount = +(rate * tables * pauseCredit).toFixed(2);
            const amountToCollect = Math.max(
              0,
              +(grossAmount - pauseDiscount).toFixed(2),
            );
            const isOverdue = curEnd && new Date(curEnd + "T00:00:00") < today;
            return (
              <div
                data-testid="payment-period-summary"
                className="rounded-lg border border-amber-400/30 bg-amber-500/5 p-3 space-y-2 text-xs"
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">
                      Current Period
                    </div>
                    <div
                      className="text-gray-200 font-semibold"
                      data-testid="current-period-range"
                    >
                      {fmt(curStart)} <span className="text-gray-500">→</span>{" "}
                      {fmt(curEnd)}
                    </div>
                    {curEnd && (
                      <div
                        className={`text-[10px] mt-0.5 ${
                          isOverdue ? "text-rose-300" : "text-emerald-300"
                        }`}
                        data-testid="paid-until-label"
                      >
                        {isOverdue ? "Overdue — paid until " : "Paid until "}
                        <b>{fmt(curEnd)}</b>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">
                      Next Period (this payment)
                    </div>
                    <div
                      className="text-amber-200 font-bold"
                      data-testid="next-period-range"
                    >
                      {fmt(nextStart)} <span className="text-gray-500">→</span>{" "}
                      {fmt(nextEnd)}
                    </div>
                    <div className="text-[10px] mt-0.5 text-gray-400">
                      Cycle: <b className="text-amber-200">{cycle} days</b>
                    </div>
                  </div>
                </div>
                {pauseCredit > 0 && (
                  <div
                    className="flex items-center justify-between text-emerald-300 text-[11px] pt-1"
                    data-testid="pause-credit-row"
                  >
                    <span>
                      Paused access credit:&nbsp;
                      <b>{pauseCredit} day{pauseCredit !== 1 ? "s" : ""}</b>
                      &nbsp;<span className="text-gray-400">
                        ({tables}×₹{rate}/day × {pauseCredit}d)
                      </span>
                    </span>
                    <span className="font-bold">
                      − {INR(pauseDiscount)}
                    </span>
                  </div>
                )}
                {grossAmount > 0 && (
                  <div
                    className="flex items-center justify-between pt-1 border-t border-white/10"
                    data-testid="amount-to-collect"
                  >
                    <span className="text-gray-300">
                      Amount to collect
                      {pauseCredit > 0 && (
                        <span className="text-[10px] text-gray-500 ml-1">
                          (gross {INR(grossAmount)})
                        </span>
                      )}
                    </span>
                    <span className="text-amber-300 font-black text-base">
                      {INR(amountToCollect)}
                    </span>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Amount ₹
              </Label>
              <Input
                data-testid="payment-amount-input"
                type="number"
                step="0.01"
                value={payment.amount}
                onChange={(e) =>
                  setPayment({ ...payment, amount: e.target.value })
                }
                className="bg-black/40 border-white/10 text-white"
              />
              {active && (() => {
                const cycle = Number(
                  active.billing_cycle_days ??
                    active.subscription?.billing_cycle_days ??
                    30,
                );
                const dailyCost = Number(active.subscription?.daily_cost || 0);
                const rate = Number(active.daily_rate || 0);
                const tables = Number(active.tables_count || 0);
                const pauseCredit = Math.max(
                  0,
                  Number(active.pending_pause_credit_days || 0),
                );
                const gross = +(dailyCost * cycle).toFixed(2);
                const discount = +(rate * tables * pauseCredit).toFixed(2);
                const suggested = Math.max(0, +(gross - discount).toFixed(2));
                if (!(gross > 0)) return null;
                const current = Number(payment.amount || 0);
                const matches = Math.abs(current - suggested) < 0.005;
                return (
                  <div
                    className="flex items-center justify-between text-[11px] text-gray-400 pt-0.5"
                    data-testid="payment-suggested-row"
                  >
                    <span>
                      Suggested:&nbsp;
                      <b className="text-amber-300">
                        {INR(suggested)}
                      </b>
                      &nbsp;
                      <span className="text-gray-500">
                        ({active.tables_count}&nbsp;×&nbsp;₹{active.daily_rate}/day × {cycle}d
                        {pauseCredit > 0 ? ` − ${pauseCredit}d paused` : ""})
                      </span>
                    </span>
                    {!matches && (
                      <button
                        type="button"
                        data-testid="use-suggested-btn"
                        onClick={() =>
                          setPayment({ ...payment, amount: String(suggested) })
                        }
                        className="text-[10px] uppercase tracking-widest text-amber-300 hover:text-amber-200 font-bold"
                      >
                        Use suggested
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Mode
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {["cash", "upi", "card"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() =>
                      setPayment({ ...payment, payment_mode: m })
                    }
                    className={`px-3 py-2 rounded-lg border-2 uppercase text-xs font-bold tracking-wider transition ${
                      payment.payment_mode === m
                        ? "border-amber-400 bg-amber-400/10 text-amber-200"
                        : "border-white/10 text-gray-400"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Notes
              </Label>
              <Input
                value={payment.notes}
                onChange={(e) =>
                  setPayment({ ...payment, notes: e.target.value })
                }
                placeholder="Optional"
                className="bg-black/40 border-white/10 text-white"
              />
            </div>

            {/* Custom deadline toggle */}
            <div className="rounded-lg border border-white/10 p-3 bg-black/30 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  data-testid="custom-deadline-toggle"
                  type="checkbox"
                  checked={payment.use_custom_deadline}
                  onChange={(e) =>
                    setPayment({
                      ...payment,
                      use_custom_deadline: e.target.checked,
                    })
                  }
                  className="w-4 h-4 accent-amber-400"
                />
                <span className="text-xs uppercase tracking-widest font-bold text-amber-200">
                  Set custom deadline
                </span>
                <span className="text-[10px] text-gray-500 normal-case tracking-normal ml-auto">
                  Override auto-calculation
                </span>
              </label>
              {payment.use_custom_deadline && (
                <div className="space-y-1.5 pt-1">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-400">
                    Extend Until
                  </Label>
                  <Input
                    data-testid="custom-deadline-input"
                    type="date"
                    min={new Date().toISOString().slice(0, 10)}
                    value={payment.extend_to}
                    onChange={(e) =>
                      setPayment({ ...payment, extend_to: e.target.value })
                    }
                    className="bg-black/40 border-white/10 text-white"
                  />
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {[7, 15, 30, 60, 90].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          const dt = new Date();
                          dt.setDate(dt.getDate() + d);
                          setPayment({
                            ...payment,
                            extend_to: dt.toISOString().slice(0, 10),
                          });
                        }}
                        className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold rounded border border-white/10 text-gray-300 hover:border-amber-400/50 hover:text-amber-200"
                      >
                        +{d}d
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {payment.amount && active && !payment.use_custom_deadline && (
              <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-md px-3 py-2 text-xs text-emerald-200">
                Extends subscription by{" "}
                <b>
                  {Math.max(
                    1,
                    Math.floor(
                      Number(payment.amount) /
                        Math.max(0.01, active.subscription?.daily_cost || 0.01)
                    )
                  )}
                </b>{" "}
                day(s)
              </div>
            )}
            {payment.use_custom_deadline && payment.extend_to && (
              <div className="bg-amber-500/10 border border-amber-400/30 rounded-md px-3 py-2 text-xs text-amber-200">
                Subscription will be set to expire on <b>{payment.extend_to}</b>{" "}
                (amount logged is informational only)
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setPayOpen(false)}
              className="text-gray-400 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={recordPayment}
              disabled={
                !payment.amount ||
                Number(payment.amount) <= 0 ||
                (payment.use_custom_deadline && !payment.extend_to)
              }
              data-testid="confirm-payment-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit cafe rate / grace */}
      <Dialog open={editCafeOpen} onOpenChange={(v) => !v && setEditCafeOpen(false)}>
        <DialogContent className="bg-[#121a16] border border-amber-400/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">Edit Daily Rate</DialogTitle>
            <DialogDescription className="text-gray-400">
              Technology usage fee for {active?.name}. Changes apply to future billing math.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                ₹ / table / day
              </Label>
              <Input
                data-testid="edit-rate-input"
                type="number"
                step="0.01"
                min="0"
                value={editCafeForm.daily_rate}
                onChange={(e) =>
                  setEditCafeForm({ ...editCafeForm, daily_rate: e.target.value })
                }
                className="bg-black/40 border-white/10 text-white"
              />
              {active?.tables_count > 0 && editCafeForm.daily_rate && (
                <div className="text-[11px] text-amber-200 mt-1">
                  ≈ ₹{(Number(editCafeForm.daily_rate) * active.tables_count).toFixed(2)}/day · ₹{(Number(editCafeForm.daily_rate) * active.tables_count * 30).toFixed(0)}/mo for {active.tables_count} tables
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Billing Cycle
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[15, 30].map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`edit-cycle-${c}`}
                    onClick={() =>
                      setEditCafeForm({ ...editCafeForm, billing_cycle_days: String(c) })
                    }
                    className={`rounded-md px-3 py-2.5 text-sm font-black uppercase tracking-widest border transition ${
                      String(editCafeForm.billing_cycle_days) === String(c)
                        ? "border-amber-400 bg-amber-500/20 text-amber-100"
                        : "border-white/10 bg-black/40 text-gray-300 hover:border-white/20"
                    }`}
                  >
                    {c} days
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-gray-500">
                Each recorded payment will run for this many days from the day it&apos;s logged.
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setEditCafeOpen(false)}
              className="text-gray-400 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={saveDailyRate}
              data-testid="save-rate-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve modal */}
      <Dialog
        open={!!approveTarget}
        onOpenChange={(v) => !v && setApproveTarget(null)}
      >
        <DialogContent className="bg-[#121a16] border border-emerald-400/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              Approve Cafe
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {approveTarget?.name} · {approveTarget?.owner_email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-gray-400">
                  Trial Days
                </Label>
                <Input
                  data-testid="approve-trial-days"
                  type="number"
                  min="0"
                  value={approveForm.trial_days}
                  onChange={(e) =>
                    setApproveForm({ ...approveForm, trial_days: e.target.value })
                  }
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-gray-400">
                  Tables to Seed
                </Label>
                <Input
                  data-testid="approve-tables-count"
                  type="number"
                  min="0"
                  value={approveForm.tables_count}
                  onChange={(e) =>
                    setApproveForm({ ...approveForm, tables_count: e.target.value })
                  }
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-md px-3 py-2 text-xs text-emerald-200">
              On approve: trial starts today, tables are seeded, and the owner
              gets an email + can log in immediately.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setApproveTarget(null)}
              className="text-gray-400 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmApprove}
              disabled={busy}
              data-testid="confirm-approve-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-black uppercase tracking-widest"
            >
              {busy ? "Approving…" : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog
        open={!!rejectTarget}
        onOpenChange={(v) => !v && setRejectTarget(null)}
      >
        <DialogContent className="bg-[#121a16] border border-rose-400/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <XCircle className="w-5 h-5 text-rose-400" />
              Reject Cafe
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {rejectTarget?.name} · {rejectTarget?.owner_email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Reason (optional, visible to the owner)
              </Label>
              <Input
                data-testid="reject-reason-input"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Missing GST details"
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div className="bg-rose-500/10 border border-rose-400/30 rounded-md px-3 py-2 text-xs text-rose-200">
              The owner will be notified by email and cannot use the product.
              They can still log in and see the rejection notice.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setRejectTarget(null)}
              className="text-gray-400 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmReject}
              disabled={busy}
              data-testid="confirm-reject-btn"
              className="bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest"
            >
              {busy ? "Rejecting…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pause-access modal */}
      <Dialog
        open={pauseOpen}
        onOpenChange={(v) => {
          if (!v) setPauseOpen(false);
        }}
      >
        <DialogContent className="bg-[#121a16] border border-amber-400/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2 text-amber-200">
              <PauseCircle className="w-5 h-5 text-amber-300" />
              Pause access
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {active?.name} · owner &amp; admin will see a block screen
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                For how long?
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map((d) => (
                  <button
                    key={d}
                    type="button"
                    data-testid={`pause-days-${d}`}
                    onClick={() => setPauseDays(d)}
                    className={`rounded-md px-3 py-3 text-sm font-black uppercase tracking-widest border transition ${
                      pauseDays === d
                        ? "border-amber-400 bg-amber-500/20 text-amber-100"
                        : "border-white/10 bg-black/40 text-gray-300 hover:border-white/20"
                    }`}
                  >
                    {d} day{d > 1 ? "s" : ""}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Reason (optional, visible on the owner&apos;s block screen)
              </Label>
              <Input
                data-testid="pause-reason-input"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="e.g. Payment dispute, end-of-day audit, etc."
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div className="bg-amber-500/10 border border-amber-400/30 rounded-md px-3 py-2 text-[12px] text-amber-100/90 leading-relaxed">
              Owner &amp; admin will see a block screen until the timer ends.
              You can resume access at any time from this same panel.
              The subscription deadline is <b>not</b> affected.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setPauseOpen(false)}
              className="text-gray-400 hover:bg-white/5"
              data-testid="pause-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={pauseCafe}
              disabled={pauseBusy}
              data-testid="pause-confirm-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              {pauseBusy ? "Pausing…" : `Pause for ${pauseDays} day${pauseDays > 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-cafe confirmation modal */}
      <Dialog
        open={deleteCafeOpen}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteCafeOpen(false);
            setDeleteCafeConfirm("");
          }
        }}
      >
        <DialogContent className="bg-[#121a16] border border-rose-500/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2 text-rose-200">
              <Trash2 className="w-5 h-5 text-rose-400" />
              Delete cafe permanently
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {active?.name} · {active?.owner_email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-rose-500/10 border border-rose-400/30 rounded-md px-3 py-3 text-[12px] text-rose-100/90 leading-relaxed">
              You are about to permanently delete{" "}
              <b className="text-white">{active?.name}</b>. This wipes the cafe,
              its {active?.tables_count ?? 0} table(s), all session history,
              players, settings, recorded payments and the owner + staff user
              accounts. <b>This cannot be undone.</b>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Type <span className="text-white font-mono">{active?.name}</span>{" "}
                to confirm
              </Label>
              <Input
                data-testid="delete-cafe-confirm-input"
                value={deleteCafeConfirm}
                onChange={(e) => setDeleteCafeConfirm(e.target.value)}
                placeholder={active?.name || ""}
                autoFocus
                className="bg-black/40 border-rose-400/30 text-white"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              data-testid="delete-cafe-cancel-btn"
              onClick={() => {
                setDeleteCafeOpen(false);
                setDeleteCafeConfirm("");
              }}
              className="text-gray-400 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={deleteCafe}
              disabled={deleteCafeBusy || deleteCafeConfirm !== active?.name}
              data-testid="delete-cafe-confirm-btn"
              className="bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-widest disabled:opacity-50"
            >
              {deleteCafeBusy ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Mini({ label, v }) {
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="text-sm font-bold text-white mt-0.5">{v}</div>
    </div>
  );
}

// Editable "Period Start" tile. Click to open a small popover with explicit
// Day / Month / Year dropdowns and an OK button — no auto-commit while
// typing, so there is zero chance of partial dates hitting the backend.
function PeriodStartTile({ value, onCommit }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const [d, m, y] = (() => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value || "")) {
      return [
        Number(value.slice(8, 10)),
        Number(value.slice(5, 7)),
        Number(value.slice(0, 4)),
      ];
    }
    return [today.getDate(), today.getMonth() + 1, today.getFullYear()];
  })();
  const [day, setDay] = useState(d);
  const [month, setMonth] = useState(m);
  const [year, setYear] = useState(y);

  useEffect(() => {
    if (open) {
      setDay(d);
      setMonth(m);
      setYear(y);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const safeDay = Math.min(day, daysInMonth);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const years = Array.from({ length: 11 }, (_, i) => today.getFullYear() - 2 + i);

  const display = /^\d{4}-\d{2}-\d{2}$/.test(value || "")
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";

  const apply = () => {
    const iso = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(safeDay).padStart(2, "0")}`;
    setOpen(false);
    if (iso !== value) onCommit(iso);
  };

  const selectCls =
    "bg-black/30 border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-amber-400";

  return (
    <div
      className="bg-black/40 border border-white/10 rounded-lg p-3 flex flex-col justify-between min-h-[68px]"
      data-testid="period-start-tile"
    >
      <div className="text-[10px] uppercase tracking-widest text-gray-500">
        Period Start
      </div>
      <button
        type="button"
        data-testid="period-start-trigger"
        onClick={() => setOpen(true)}
        className="text-left mt-1 text-sm font-bold text-white hover:text-amber-200"
      >
        {display}
      </button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="bg-[#121a16] border border-amber-400/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-black">Set Period Start</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              The period end is recalculated automatically using the billing cycle.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 py-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Day</div>
              <select
                data-testid="period-start-day"
                value={safeDay}
                onChange={(e) => setDay(Number(e.target.value))}
                className={`${selectCls} w-full`}
              >
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Month</div>
              <select
                data-testid="period-start-month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className={`${selectCls} w-full`}
              >
                {months.map((label, idx) => (
                  <option key={label} value={idx + 1}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Year</div>
              <select
                data-testid="period-start-year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className={`${selectCls} w-full`}
              >
                {years.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              data-testid="period-start-ok"
              onClick={apply}
              className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
